import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Plate, Chip, Empty, Field, NSelect, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, fDate, fNum, fEur, todayIso } from "@/lib/api";

export const M_STATUS = {
  en_retard: "bg-rose-50 text-rose-700 border-rose-200",
  bientot: "bg-amber-50 text-amber-700 border-amber-200",
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function addMonths(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(y, m - 1 + n, 1);
  const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
  const pad = (x) => String(x).padStart(2, "0");
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(Math.min(d, last))}`;
}

export function MaintenanceDialog({ entry, vehicles, onClose, onSaved }) {
  const types = useFetch("/maintenance/types");
  const [f, setF] = useState({ vehicle_id: vehicles.length === 1 ? vehicles[0].id : "", type: "revision", date: todayIso(), mileage: "", cost: "", provider: "", comment: "", next_due_date: "", next_due_km: "", ...entry, ...(entry?.id && !entry.date ? { date: "" } : {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (x) => setF({ ...f, [k]: x?.target ? x.target.value : x });
  const suggest = () => {
    const t = (types.data || []).find((x) => x.value === f.type);
    if (!t) return;
    setF({ ...f, next_due_date: t.months && f.date ? addMonths(f.date, t.months) : "", next_due_km: t.km && f.mileage ? Number(f.mileage) + t.km : "" });
  };
  const save = async () => {
    setBusy(true);
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const body = { ...f, date: f.date || null, mileage: num(f.mileage), cost: num(f.cost), next_due_km: num(f.next_due_km), next_due_date: f.next_due_date || null };
    try {
      if (entry?.id) await api.put(`/maintenance/${entry.id}`, body);
      else await api.post("/maintenance", body);
      toast.success("Entretien enregistré");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="maintenance-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#0E2D43]">{entry?.id ? "Modifier l'entretien" : "Nouvel entretien"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Véhicule"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} placeholder="Choisir…" testid="maintenance-vehicle-select"
            options={vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.brand} ${v.model}` }))} /></Field>
          <Field label="Type d'entretien"><NSelect value={f.type} onChange={set("type")} testid="maintenance-type-select" options={types.data || []} /></Field>
          <Field label="Date de l'intervention"><Input type="date" max={todayIso()} value={f.date} onChange={set("date")} data-testid="maintenance-date-input" /></Field>
          <Field label="Kilométrage"><Input type="number" value={f.mileage ?? ""} onChange={set("mileage")} data-testid="maintenance-mileage-input" /></Field>
          <Field label="Coût TTC (€)"><Input type="number" step="0.01" value={f.cost ?? ""} onChange={set("cost")} data-testid="maintenance-cost-input" /></Field>
          <Field label="Prestataire / garage"><Input value={f.provider || ""} onChange={set("provider")} data-testid="maintenance-provider-input" /></Field>
        </div>
        <div className="rounded-lg bg-[#FEF3E2] border border-[#F49619]/30 p-3 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <Field label="Prochaine échéance (date)"><Input type="date" value={f.next_due_date || ""} onChange={set("next_due_date")} className="bg-white" data-testid="maintenance-next-date-input" /></Field>
          <Field label="Prochaine échéance (km)"><Input type="number" value={f.next_due_km ?? ""} onChange={set("next_due_km")} className="bg-white" data-testid="maintenance-next-km-input" /></Field>
          <Button type="button" variant="outline" onClick={suggest} className="bg-white" data-testid="maintenance-suggest-button">Proposer</Button>
        </div>
        <Field label="Commentaire"><Textarea rows={2} value={f.comment} onChange={set("comment")} data-testid="maintenance-comment-input" /></Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="maintenance-save-button">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const left = (r) => [r.days_left !== null ? (r.days_left < 0 ? `${-r.days_left} j de retard` : `dans ${r.days_left} j`) : null,
  r.km_left !== null ? (r.km_left < 0 ? `${fNum(-r.km_left)} km dépassés` : `dans ${fNum(r.km_left)} km`) : null].filter(Boolean).join(" · ");

export function RemindersTable({ rows, showPlate = true }) {
  if (!rows.length) return <Empty text="Aucune échéance enregistrée" />;
  return (
    <div className="overflow-x-auto">
      <table className="tbl w-full" data-testid="reminders-table">
        <thead><tr><th>Statut</th>{showPlate && <th>Véhicule</th>}<th>Entretien</th><th>Dernier</th><th>Échéance date</th><th className="text-right">Échéance km</th><th>Reste</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`reminder-row-${r.id}`}>
              <td><Chip cls={M_STATUS[r.status]}>{r.status_label}</Chip></td>
              {showPlate && <td><div className="flex items-center gap-2"><Plate>{r.plate}</Plate><span className="text-xs text-slate-500">{r.vehicle_label}</span></div></td>}
              <td className="font-semibold">{r.type_label}</td>
              <td>{fDate(r.last_date)}{r.last_mileage ? <span className="text-slate-400"> · {fNum(r.last_mileage)} km</span> : null}</td>
              <td>{fDate(r.next_due_date)}</td>
              <td className="text-right tabular-nums">{r.next_due_km ? fNum(r.next_due_km) : "—"}</td>
              <td className={r.status === "en_retard" ? "text-rose-700 font-semibold" : "text-slate-600"}>{left(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HistoryTable({ rows, admin, onEdit, onDelete, showPlate = true }) {
  if (!rows.length) return <Empty text="Aucun entretien enregistré" />;
  return (
    <div className="overflow-x-auto">
      <table className="tbl w-full" data-testid="maintenance-table">
        <thead><tr><th>Date</th>{showPlate && <th>Véhicule</th>}<th>Type</th><th className="text-right">Km</th><th className="text-right">Coût</th><th>Prestataire</th><th>Prochaine échéance</th><th>Commentaire</th>{admin && <th></th>}</tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id} data-testid={`maintenance-row-${m.id}`}>
              <td>{fDate(m.date)}</td>
              {showPlate && <td><Plate>{m.plate}</Plate></td>}
              <td className="font-semibold">{m.type_label}</td>
              <td className="text-right tabular-nums">{fNum(m.mileage)}</td>
              <td className="text-right tabular-nums">{m.cost !== null && m.cost !== undefined ? fEur(m.cost) : "—"}</td>
              <td>{m.provider || "—"}</td>
              <td>{[m.next_due_date && fDate(m.next_due_date), m.next_due_km && `${fNum(m.next_due_km)} km`].filter(Boolean).join(" · ") || "—"}</td>
              <td className="max-w-[220px] truncate text-slate-500">{m.comment || "—"}</td>
              {admin && (
                <td><div className="flex gap-1">
                  <button onClick={() => onEdit(m)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`maintenance-edit-${m.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(m)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`maintenance-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                </div></td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
